/**
 * Polyfills for what pdf.js 6 (the Documents viewers) uses before every browser has it, so PDFs draw on browsers a
 * year or two old: Map/WeakMap getOrInsert(Computed) and Math.sumPrecise (Chrome 141 has neither; every page failed to
 * draw), Promise.try, Promise.withResolvers, Uint8Array.fromBase64 / toBase64 / toHex. main.tsx imports this first;
 * the build also compiles it in front of pdf.js's worker (scripts/build-client.mjs).
 */
const define = (target: object, name: string, value: unknown) => {
  if (!(name in target)) Object.defineProperty(target, name, { configurable: true, writable: true, value });
};
type Upsert = { has(k: unknown): boolean; get(k: unknown): unknown; set(k: unknown, v: unknown): unknown };
for (const proto of [Map.prototype, WeakMap.prototype] as unknown as Upsert[]) {
  define(proto, "getOrInsert", function (this: Upsert, k: unknown, v: unknown) { if (!this.has(k)) this.set(k, v); return this.get(k); });
  define(proto, "getOrInsertComputed", function (this: Upsert, k: unknown, f: (k: unknown) => unknown) { if (!this.has(k)) this.set(k, f(k)); return this.get(k); });
}
// Neumaier's compensated sum: exact enough for pdf.js's glyph widths and matrices.
define(Math, "sumPrecise", (items: Iterable<number>) => {
  let sum = 0, c = 0, any = false;
  for (const x of items) {
    if (typeof x !== "number") throw new TypeError("Math.sumPrecise: not a number");
    any = true;
    const t = sum + x;
    c += Math.abs(sum) >= Math.abs(x) ? (sum - t) + x : (x - t) + sum;
    sum = t;
  }
  return any ? sum + c : -0;
});
define(Promise, "try", <T,>(f: (...a: unknown[]) => T, ...args: unknown[]) => new Promise<T>((resolve) => resolve(f(...args))));
define(Promise, "withResolvers", () => {
  let resolve!: (v: unknown) => void, reject!: (e: unknown) => void;
  const promise = new Promise((a, b) => { resolve = a; reject = b; });
  return { promise, resolve, reject };
});
define(Uint8Array, "fromBase64", (s: string) => Uint8Array.from(atob(String(s).replace(/[\s=]+$/g, "").replace(/-/g, "+").replace(/_/g, "/")), (ch) => ch.charCodeAt(0)));
define(Uint8Array.prototype, "toBase64", function (this: Uint8Array) {
  let bin = "";
  for (let i = 0; i < this.length; i += 0x8000) bin += String.fromCharCode(...this.subarray(i, i + 0x8000));
  return btoa(bin);
});
define(Uint8Array.prototype, "toHex", function (this: Uint8Array) { return Array.from(this, (b) => b.toString(16).padStart(2, "0")).join(""); });
export {};
